import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useRecoilState, useRecoilValue } from 'recoil';
import { object, string } from 'yup';

import { ENVIRONMENT } from '@constants';
import { environmentsState } from '@store';

import { apiConfig, updateApiConfig, updateApiKeyConfig } from './apiConfig';

import { RadialGradientBackground } from '../components/loading/RadialGradientBackground';
import { getDspAccessToken } from '../helpers/DspAuth';
import { getStorageItem } from '../helpers/storage';

export const ConfigGateway = ({ children }) => {
  const [canGetKeyData, setCanGetKeyData] = useState(false);
  const [configComplete, setConfigComplete] = useState(false);
  const {
    environments: { prod, debug, qa },
  } = useRecoilValue(environmentsState);

  const [envData, setEnvData] = useRecoilState(environmentsState);

  const [isCheckedEnv, setIsCheckedEnv] = useState(false);

  useEffect(() => {
    // Check the storage config only when the app starts
    // because when switching environments from the Debug Menu
    // automatically will set the new states
    if (envData.overrideEnv) {
      const storageEnvData = JSON.parse(getStorageItem(ENVIRONMENT));

      if (storageEnvData?.overrideEnv) {
        setEnvData({ ...envData, ...storageEnvData });
        setIsCheckedEnv(true);
      } else {
        setIsCheckedEnv(true);
      }
    } else {
      setIsCheckedEnv(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data } = useQuery(['getRemoteConfig', prod], () => getRemoteConfig(prod, qa), {
    refetchOnWindowFocus: false,
    enabled: isCheckedEnv,
    retry: true,
    onSuccess: () => {
      setCanGetKeyData(false);
    },
  });

  const { data: keyData } = useQuery(['getRemoteKeyConfig', debug, prod], getRemoteKeyConfig, {
    refetchOnWindowFocus: false,
    retry: true,
    enabled: canGetKeyData,
  });

  useEffect(() => {
    if (data && !canGetKeyData) {
      updateApiConfig(data);
      setCanGetKeyData(true);
    }

    if (keyData) {
      updateApiKeyConfig(keyData);
      setConfigComplete(true);
      setCanGetKeyData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, keyData, prod]);

  return configComplete ? children : <RadialGradientBackground />;
};

const configSchema = object({
  constants: object({
    dsp_token_key: string(),
    register_device_url: string(),
    web_signup_url: string(),
    vizio_signup_url: string().nullable(),
    x1_signup_url: string().nullable(),
  }),
  api: object({
    dsp: object({
      base: string(),
      image_host: string(),
      video_stream: string(),
      live_channel_info: string(),
      onboard_landing: string(),
      channel_info: string(),
      channels: string(),
      user_products: string(),
      subscriptions_summary: string(),
      vod_landing: string(),
      channel_details: string(),
      recently_watched_data: string(),
      recently_watched_point_get: string(),
      recently_watched_point_add: string(),
      watchlist_get: string(),
      watchlist_add: string(),
      watchlist_remove: string(),
      search_videos: string(),
      search_channels: string(),
      token: string(),
      token_refresh: string(),
      adobe_token: string(),
      amazon_receipt_validation: string(),
      onboard_choose_plan: string(),
    }),
    twc: object({
      base_v1: string(),
      base_v2: string(),
      base_v3: string(),
      air_quality: string(),
      current_on_demand: string(),
      hourly_forecast: string(),
      daily_forecast: string(),
      alerts_headlines: string(),
      alerts_detail: string(),
      pollen_index: string(),
      us_lightning: string(),
      map_tile_inventory: string(),
      map_tiles: string(),
    }),
    mapbox: object({
      base: string(),
      places: string(),
      base_tile_template_react: string(),
      base_tile_template_roku: string(),
      symbol_tile_template: string(),
      style_url: string(),
    }),
    adobe_tve: object({
      base: string(),
      o_register: string(),
      o_access_token: string(),
      regcode: string(),
      checkauthn: string(),
      authorize: string(),
      logout: string(),
      mvpd_list: string(),
    }),
    auth0: object({
      base: string(),
      device_code: string(),
      token: string(),
      start: string(),
    }),
    segment: object({
      base: string(),
      identify: string(),
      track: string(),
    }),
    localnow: object({
      base: string(),
      city: string(),
    }),
    anf: object({
      anf_endpoint: string(),
      anf_poll_interval: string(),
    }),
    vizio: object({
      appBundle: string(),
    }),
  }),
});

const getRemoteConfig = async (isProduction, isQA) => {
  const { data } = await axios.get(
    isProduction
      ? apiConfig.REMOTE_CONFIG_PROD_URL
      : isQA
      ? apiConfig.REMOTE_CONFIG_QA_URL
      : apiConfig.REMOTE_CONFIG_DEV_URL,
  );

  return configSchema.validate(data);
};

const getRemoteKeyConfig = async (retry = false) => {
  const { accessToken, error } = await getDspAccessToken(retry);

  if (error) {
    return error;
  }

  const headers = {
    'x-access-token': accessToken,
  };

  try {
    const { data } = await axios.get(apiConfig.DSP_KEY_CONFIG_URL, {
      headers,
    });

    return data;
  } catch (requestError) {
    if (retry) {
      throw requestError;
    }
    return getRemoteKeyConfig(true);
  }
};
